import React, { createContext, useState, useEffect, useCallback } from "react";
import { getContratos } from "../application/contratos";
import { getLocadores } from "../application/locadores";
import { getLocatarios } from "../application/locatarios";
import { getConfigAlquileres } from "../application/configuracion";
import { getPagos } from "../application/pagos";
import { getIndices } from "../application/indices";
import { getLiquidaciones } from "../application/liquidaciones";
import { getGastos } from "../application/gastos";
import { getAumentosAplicados } from "../application/aumentosAplicados";

export const AlquileresContext = createContext();

const AlquileresContextProvider = ({ children }) => {
  const [contratos, setContratos] = useState([]);
  const [locadores, setLocadores] = useState([]);
  const [locatarios, setLocatarios] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [indices, setIndices] = useState([]);
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [gastos, setGastos] = useState([]);
  const [aumentosAplicados, setAumentosAplicados] = useState([]);
  const [config, setConfig] = useState({
    comisionPorcentaje: 5,
    diasAlertaVencimiento: 30,
  });
  const [loading, setLoading] = useState(true);
  // True after the very first fetchAll completes. Used by the route guard to
  // show the spinner only on initial mount, not on subsequent refreshes
  // (post-import, post-migration) where keeping the page mounted matters.
  const [initialLoaded, setInitialLoaded] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [contratosData, locadoresData, locatariosData, configData, pagosData, indicesData, liquidacionesData, gastosData, aumentosAplicadosData] =
        await Promise.all([
          getContratos(),
          getLocadores(),
          getLocatarios(),
          getConfigAlquileres(),
          getPagos(),
          getIndices(),
          getLiquidaciones(),
          getGastos(),
          getAumentosAplicados(),
        ]);
      setContratos(contratosData);
      setLocadores(locadoresData);
      setLocatarios(locatariosData);
      setConfig(configData);
      setPagos(pagosData);
      setIndices(indicesData);
      setLiquidaciones(liquidacionesData);
      setGastos(gastosData);
      setAumentosAplicados(aumentosAplicadosData);
    } catch (error) {
      console.error("Error al cargar datos de alquileres:", error);
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const refreshContratos = async () => {
    const data = await getContratos();
    setContratos(data);
  };

  const refreshLocadores = async () => {
    const data = await getLocadores();
    setLocadores(data);
  };

  const refreshLocatarios = async () => {
    const data = await getLocatarios();
    setLocatarios(data);
  };

  const refreshConfig = async () => {
    const data = await getConfigAlquileres();
    setConfig(data);
  };

  const refreshPagos = async () => {
    const data = await getPagos();
    setPagos(data);
  };

  const refreshIndices = async () => {
    const data = await getIndices();
    setIndices(data);
  };

  const refreshLiquidaciones = async () => {
    const data = await getLiquidaciones();
    setLiquidaciones(data);
  };

  const refreshGastos = async () => {
    const data = await getGastos();
    setGastos(data);
  };

  const refreshAumentosAplicados = async () => {
    const data = await getAumentosAplicados();
    setAumentosAplicados(data);
  };

  const contratosActivos = contratos.filter((c) => c.estado === "ACTIVO");

  const contratosPorVencer = contratos.filter((c) => {
    if (c.estado !== "ACTIVO" || !c.fechaVencimiento) return false;
    const hoy = new Date();
    const vencimiento = new Date(c.fechaVencimiento);
    const diasRestantes = Math.ceil(
      (vencimiento - hoy) / (1000 * 60 * 60 * 24)
    );
    return diasRestantes >= 0 && diasRestantes <= config.diasAlertaVencimiento;
  });

  const contratosConAumentoProximo = contratos.filter((c) => {
    if (c.estado !== "ACTIVO" || !c.aumento?.proximoAumento) return false;
    const hoy = new Date();
    const proximo = new Date(c.aumento.proximoAumento);
    const diasRestantes = Math.ceil(
      (proximo - hoy) / (1000 * 60 * 60 * 24)
    );
    return diasRestantes >= 0 && diasRestantes <= 30;
  });

  return (
    <AlquileresContext.Provider
      value={{
        contratos,
        locadores,
        locatarios,
        pagos,
        indices,
        liquidaciones,
        gastos,
        aumentosAplicados,
        config,
        loading,
        initialLoaded,
        contratosActivos,
        contratosPorVencer,
        contratosConAumentoProximo,
        refreshContratos,
        refreshLocadores,
        refreshLocatarios,
        refreshConfig,
        refreshPagos,
        refreshIndices,
        refreshLiquidaciones,
        refreshGastos,
        refreshAumentosAplicados,
        refreshAll: fetchAll,
      }}
    >
      {children}
    </AlquileresContext.Provider>
  );
};

export default AlquileresContextProvider;
